/**
 * Students Page
 * List and manage all students
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useViewMode } from '@/contexts/ViewModeContext';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Input } from '@/components/common/Input';
import { ViewModeToggle } from '@/components/common/ViewModeToggle';
import { apiRequest } from '@/api/client';

interface Student {
  studentId: string;
  firstName: string;
  lastName: string;
  username: string;
  className: string;
  classId: string;
  lastActive?: string;
  storiesCreated?: number;
}

export const StudentsPage = () => {
  const { user } = useAuth();
  const { viewMode } = useViewMode();
  const navigate = useNavigate();
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchStudents();
  }, [viewMode]);

  useEffect(() => {
    if (searchTerm) {
      setFilteredStudents(
        students.filter(s =>
          s.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.className.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredStudents(students);
    }
  }, [searchTerm, students]);

  const fetchStudents = async () => {
    try {
      setIsLoading(true);
      
      if (user?.isAdmin && viewMode === 'school') {
        // Admin viewing all school students
        const studentsResponse = await apiRequest<{ students: Student[] }>('get_school_students', {
          adminEmail: user?.email,
          schoolId: user?.schoolId,
        });
        
        if (studentsResponse.success && (studentsResponse as any).students) {
          const students = (studentsResponse as any).students;
          
          // Students already have currentClassName from the database
          const studentsWithClass = students.map((s: any) => ({
            ...s,
            className: s.currentClassName || 'No Class',
            classId: s.currentClassId || null,
          }));
          
          setStudents(studentsWithClass);
          setFilteredStudents(studentsWithClass);
        }
      } else {
        // Personal view - get teacher's classes first, then aggregate students
        const classesResponse = await apiRequest<{ classes: any[] }>('get_teacher_classes', {
          userEmail: user?.email,
        });
        
        const classes = classesResponse.success && (classesResponse as any).classes 
          ? (classesResponse as any).classes 
          : [];
        
        // Aggregate students from all teacher's classes
        const studentMap = new Map();
        
        for (const classItem of classes) {
          try {
            const classStudentsResponse = await apiRequest<{ students: Student[] }>('get_class_students', {
              classId: classItem.classId,
            });
            
            if (classStudentsResponse.success && (classStudentsResponse as any).students) {
              for (const student of (classStudentsResponse as any).students) {
                // Use Map to avoid duplicates (students can be in multiple classes)
                if (!studentMap.has(student.studentId)) {
                  studentMap.set(student.studentId, {
                    ...student,
                    className: student.currentClassName || classItem.className,
                    classId: student.currentClassId || classItem.classId,
                  });
                }
              }
            }
          } catch (error) {
            console.error(`Failed to fetch students for class ${classItem.classId}:`, error);
          }
        }
        
        const allStudents = Array.from(studentMap.values());
        setStudents(allStudents);
        setFilteredStudents(allStudents);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Students</h1>
          <p className="text-slate-600 mt-2">Manage student accounts and progress</p>
        </div>
        <div className="flex items-center gap-3">
          <ViewModeToggle />
          <Button variant="primary" onClick={() => navigate('/students/new')}>
            ➕ Add Student
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <div className="p-4">
          <Input
            type="text"
            placeholder="Search students by name, username, or class..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </Card>

      {/* Students List */}
      {filteredStudents.length === 0 ? (
        <Card>
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">👥</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {searchTerm ? 'No students found' : 'No students yet'}
            </h3>
            <p className="text-slate-600 mb-6">
              {searchTerm
                ? 'Try adjusting your search'
                : 'Add students to your classes to get started'}
            </p>
            {!searchTerm && (
              <Button variant="primary" onClick={() => navigate('/students/new')}>
                Add Your First Student
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Class
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Year
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Stories
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredStudents.map((student) => (
                  <tr key={student.studentId} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-semibold">
                            {student.firstName[0]}{student.lastName[0]}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-slate-900">
                            {student.firstName} {student.lastName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900 font-mono">{student.username}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">{student.className}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">
                        {(student as any).currentYearGroup ? `Year ${(student as any).currentYearGroup}` : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">{student.storiesCreated || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/students/${student.studentId}`)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};
